import type { ButtonProps, TypographyProps } from '@mui/material';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import { createContext, useContext } from 'react';
import type { ResultContextValue, Size } from './types';

export const ResultContext = createContext<ResultContextValue | undefined>(undefined);

const SPACING = capitalDesignTokens.foundations.layout.space;

export const sizeSpecificStyles = {
  small: {
    spacing: `${SPACING.xsmall}px`,
    headerSize: 'h4' as TypographyProps['variant'],
    subTitleSize: 'bodyXSmall' as TypographyProps['variant'],
    iconSize: '16px',
    buttonSize: 'small' as ButtonProps['size']
  },
  medium: {
    spacing: `${SPACING.small}px`,
    headerSize: 'h3' as TypographyProps['variant'],
    subTitleSize: 'bodySmall' as TypographyProps['variant'],
    iconSize: '24px',
    buttonSize: 'small' as ButtonProps['size']
  },
  large: {
    spacing: `${SPACING.small}px`,
    headerSize: 'h2' as TypographyProps['variant'],
    subTitleSize: 'bodyDefault' as TypographyProps['variant'],
    iconSize: '32px',
    buttonSize: 'medium' as ButtonProps['size']
  },
  xlarge: {
    spacing: `${SPACING.small}px`,
    headerSize: 'h1' as TypographyProps['variant'],
    subTitleSize: 'bodyDefault' as TypographyProps['variant'],
    iconSize: '40px',
    buttonSize: 'large' as ButtonProps['size']
  }
};

export const sizeStyles = (size: Size, isMobile: boolean) => {
  if (isMobile) {
    return {
      ...sizeSpecificStyles.small,
      buttonSize: 'medium' as ButtonProps['size']
    };
  }
  return sizeSpecificStyles[size];
};

/**
 * Hook to access the Result context value. Throws if used outside a Result provider.
 */
export const useResultContext = () => {
  const ctx = useContext(ResultContext);
  if (!ctx) {
    throw new Error('useResultContext must be used within a <ResultComposable> or <ResultBase> provider');
  }
  return ctx;
};
